/**
 * QueueStatusBadge — shows pending and failed queue entries for a given entityId.
 * Compact inline component designed to sit alongside action buttons in detail panels.
 * Polls every 8s while there are active (queued/leased) entries.
 */
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import { Box, Chip, CircularProgress, Tooltip, Typography } from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useRef, useState } from 'react';

export interface QueueEntry {
  queueId: string;
  jobType: string;
  status: 'queued' | 'leased' | 'dead';
  attempts: number;
  maxAttempts: number;
  lastError: string | null;
  createdAt: string;
}

interface QueueStatusBadgeProps {
  entityId: string;
  /** Restrict to specific jobTypes. If omitted, shows all types for this entity. */
  jobTypes?: string[];
  /** Called after every fetch so parent can react (e.g. refresh data when queue drains) */
  onDrained?: () => void;
}

const JOB_TYPE_LABEL: Record<string, string> = {
  BATCH_CANDIDATES_FOR_JOB: 'AI Scoring',
  REFRESH_SKILL_PROFILE: 'Skill Profile',
  EXTRACT_RESUME: 'Resume Extract',
  EXTRACT_RESUME_BULK: 'Bulk Extract',
  RERANK_JOBS_FOR_SEEKER: 'Job Ranking',
};

const POLL_INTERVAL = 8_000;

const QueueStatusBadge: React.FC<QueueStatusBadgeProps> = ({
  entityId,
  jobTypes,
  onDrained,
}) => {
  const [entries, setEntries] = useState<QueueEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevHadActive = useRef(false);

  const fetchEntries = useCallback(async () => {
    try {
      const params: Record<string, string> = {};
      if (jobTypes?.length) params.jobTypes = jobTypes.join(',');
      const { data } = await axios.get(`/api/queue/entity/${entityId}`, { params });
      // `?? []` only guards null. An error envelope or a proxy page comes back
      // as an object, which used to reach the render and throw on .filter —
      // taking the whole workspace down over a non-critical badge.
      const list: QueueEntry[] = Array.isArray(data) ? data : [];
      setEntries(list);

      const hasActive = list.some((e) => e.status === 'queued' || e.status === 'leased');
      if (prevHadActive.current && !hasActive && onDrained) {
        onDrained();
      }
      prevHadActive.current = hasActive;

      // Keep polling while there are active entries
      if (hasActive) {
        pollRef.current = setTimeout(fetchEntries, POLL_INTERVAL);
      }
    } catch {
      // Silently ignore — badge is non-critical
    } finally {
      setLoading(false);
    }
  }, [entityId, jobTypes, onDrained]);

  useEffect(() => {
    setLoading(true);
    fetchEntries();
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [fetchEntries]);

  if (loading) {
    return <CircularProgress size={12} sx={{ color: 'var(--color-text-muted)', ml: 0.5 }} />;
  }

  const active = entries.filter((e) => e.status === 'queued' || e.status === 'leased');
  const failed = entries.filter((e) => e.status === 'dead');

  if (active.length === 0 && failed.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
      {active.length > 0 && (
        <Tooltip
          title={
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                {active.length} task{active.length > 1 ? 's' : ''} in queue
              </Typography>
              {active.map((e) => (
                <Box key={e.queueId} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                  <HourglassTopIcon sx={{ fontSize: 11, color: 'var(--amber)' }} />
                  <Typography variant="caption">
                    {JOB_TYPE_LABEL[e.jobType] ?? e.jobType}
                    {e.status === 'leased' ? ': processing…' : ': waiting…'}
                  </Typography>
                </Box>
              ))}
            </Box>
          }
          arrow
        >
          <Chip
            size="small"
            icon={<HourglassTopIcon sx={{ fontSize: '12px !important' }} />}
            label={`${active.length} pending`}
            sx={{
              height: 20,
              fontSize: 'var(--font-size-3xs)',
              fontWeight: 700,
              bgcolor: 'var(--amber-tint)',
              color: 'var(--amber)',
              border: '1px solid var(--amber-tint)',
              '& .MuiChip-icon': { color: 'var(--amber)', ml: 0.5 },
              cursor: 'default',
            }}
          />
        </Tooltip>
      )}

      {failed.length > 0 && (
        <Tooltip
          title={
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                {failed.length} failed task{failed.length > 1 ? 's' : ''}
              </Typography>
              {failed.map((e) => (
                <Box key={e.queueId} sx={{ mb: 0.5 }}>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 500 }}>
                    {JOB_TYPE_LABEL[e.jobType] ?? e.jobType}: {e.attempts}/{e.maxAttempts} attempts
                  </Typography>
                  {e.lastError && (
                    <Typography variant="caption" sx={{ display: 'block', color: 'var(--red)', fontStyle: 'italic' }}>
                      {e.lastError.slice(0, 120)}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          }
          arrow
        >
          <Chip
            size="small"
            icon={<ErrorOutlineIcon sx={{ fontSize: '12px !important' }} />}
            label={`${failed.length} failed`}
            sx={{
              height: 20,
              fontSize: 'var(--font-size-3xs)',
              fontWeight: 700,
              bgcolor: 'var(--red-tint)',
              color: 'var(--red)',
              border: '1px solid var(--red-tint)',
              '& .MuiChip-icon': { color: 'var(--red)', ml: 0.5 },
              cursor: 'default',
            }}
          />
        </Tooltip>
      )}
    </Box>
  );
};

export default QueueStatusBadge;
